import React, {Fragment} from "react";
import {format} from "date-fns";
import {Meta} from "@directus/sdk";
import {Link, useLoaderData, useOutletContext} from "@remix-run/react";
import {json} from "@remix-run/node";
import type {MetaFunction, LoaderFunction, LinksFunction} from "@remix-run/node";
import {Breadcrumbs, Pagination, PostCard, OffersSlider} from "@bettorsignals/ui";
import {Collection, isPreview, Status} from "@bettorsignals/api";
import type {Preview} from "@bettorsignals/api";
import {$api} from "~/api";
import {LinkTo} from "~/utils/LinkTo";
import type {WithOffersOutletContext} from "~/routes/__with-offers";
import {useLinkTo} from "~/hooks/useLinkTo";

interface LoaderData {
    previews: Preview[];
    pagesCount: number;
    currentPage: number;
}

export const links: LinksFunction = () => {
    return [{rel: "canonical", href: `${LinkTo.fullPageUrl()}${LinkTo.nfl().previews}`}];
};

export const loader: LoaderFunction = async ({request}) => {
    const POSTS_PER_PAGE = 8;

    const pageUrlParameter = new URL(request.url).searchParams.get("page");
    const currentPage = (pageUrlParameter && Number.parseInt(pageUrlParameter)) || 1;

    const previewsResponse = await $api.items(Collection.Previews).readByQuery({
        filter: {
            status: {
                _eq: Status.Published
            },
            category: {
                _eq: "nfl"
            }
        },
        sort: ["-datePublished"],
        meta: Meta.FILTER_COUNT,
        limit: POSTS_PER_PAGE,
        page: currentPage
    });

    const pagesCount = Math.ceil(previewsResponse.meta?.filter_count! / POSTS_PER_PAGE);

    return json<LoaderData>({
        previews: previewsResponse.data?.filter(isPreview) ?? [],
        currentPage,
        pagesCount
    });
};

export const meta: MetaFunction = () => {
    return {
        title: "This Week's NFL Previews and Predictions | BettorSignals.com",
        description:
            "Click here to get free NFL expert predictions. Weekly analysis of NFL matches, injury reports, and picks for upcoming games."
    };
};

export default function NFLPreviews() {
    const {previews, pagesCount, currentPage} = useLoaderData<LoaderData>();
    const {offers} = useOutletContext<WithOffersOutletContext>();
    const {linkTo} = useLinkTo();

    return (
        <>
            <div className="flex flex-col gap-8">
                <Breadcrumbs
                    pages={[
                        {
                            name: "Home",
                            href: LinkTo.homePage()
                        },
                        // TODO
                        // {
                        //     name: "NFL",
                        //     href: LinkTo.nfl().root
                        // },
                        {
                            name: "NFL Previews",
                            href: LinkTo.nfl().previews
                        }
                    ]}
                />
            </div>

            <h1 className="mt-14 text-2xl font-bold leading-7 text-gray-900">
                NFL Previews & Predictions
            </h1>

            <div className="my-[42px] flex flex-col gap-[62px]">
                {previews.map((preview, index, thisArray) => {
                    const OFFERS_SLIDER_RENDER_INDEX = Math.min(thisArray.length - 1, 2);

                    const shouldRenderOffersSlider =
                        offers.length > 0 && index === OFFERS_SLIDER_RENDER_INDEX;

                    return (
                        <Fragment key={preview.id}>
                            <PostCard
                                date={format(new Date(preview.datePublished), "MMM d, yyyy")}
                                title={preview.title}
                                coverImageUrl={linkTo.asset(preview.cover, {
                                    height: 152,
                                    quality: 100,
                                    seoFriendlyName: `${preview.title} cover image`
                                })}
                                description={preview.description}
                                linkCta="Read Preview"
                                href={LinkTo.nfl().preview(preview.slug)}
                            />
                            {shouldRenderOffersSlider && (
                                <div className="block md:hidden">
                                    <OffersSlider offers={offers} />
                                </div>
                            )}
                        </Fragment>
                    );
                })}

                <div className="prose max-w-none">
                    <p>
                        BettorSignals is here to help you make the best possible decisions when it
                        comes to your betting choices.
                    </p>
                    <p>
                        Our weekly previews will give you expert predictions on every team in the
                        league, so you can make sure you&apos;re making informed decisions.
                    </p>

                    <p>
                        With our expert NFL previews, you&apos;ll be able to keep your finger on the
                        pulse of every game.
                    </p>
                    <p>
                        We are committed to providing you with high quality content and tools that
                        will help you hit big this season, so check back here every Tuesday for our
                        newest updates! Or head over to our{" "}
                        <Link to={LinkTo.homePage()}>daily betting signals page</Link> for daily NFL
                        picks.
                    </p>
                </div>
            </div>

            <div className="mb-12 md:mb-24">
                <Pagination pagesToDisplay={5} pagesCount={pagesCount} currentPage={currentPage} />
            </div>
        </>
    );
}
